/**
 * DateTimePicker
 *
 * Wraps react-datepicker with app styling. Supports optional time selection.
 * Replaces native `datetime-local` / `date` inputs throughout the app.
 */

import ReactDatePicker from 'react-datepicker';
import { CalendarDays, Clock, X } from 'lucide-react';
import 'react-datepicker/dist/react-datepicker.css';
import './DateTimePicker.css';

interface DateTimePickerProps {
  value: Date | undefined;
  onChange: (date: Date | undefined) => void;
  showTime?: boolean;
  placeholder?: string;
  minDate?: Date;
  maxDate?: Date;
  disabled?: boolean;
  className?: string;
}

export function DateTimePicker({
  value,
  onChange,
  showTime = true,
  placeholder,
  minDate,
  maxDate,
  disabled,
  className = '',
}: DateTimePickerProps) {
  const defaultPlaceholder = showTime ? 'Pick date & time…' : 'Pick a date…';

  return (
    <div className={`relative ${className}`}>
      <ReactDatePicker
        selected={value ?? null}
        onChange={(date: Date | null) => onChange(date ?? undefined)}
        showTimeSelect={showTime}
        timeFormat="h:mm aa"
        timeIntervals={15}
        dateFormat={showTime ? 'MMM d, yyyy h:mm aa' : 'MMM d, yyyy'}
        placeholderText={placeholder ?? defaultPlaceholder}
        minDate={minDate}
        maxDate={maxDate}
        disabled={disabled}
        popperPlacement="bottom-start"
        popperModifiers={[
          {
            name: 'preventOverflow',
            options: { boundary: 'viewport' },
          } as Parameters<typeof ReactDatePicker>[0]['popperModifiers'][0],
        ]}
        customInput={
          <CustomInput
            showTime={showTime}
            hasValue={Boolean(value)}
            onClear={() => onChange(undefined)}
            disabled={disabled}
          />
        }
      />
    </div>
  );
}

// --------------------------------------------------------------------------
// Internal styled input button
// --------------------------------------------------------------------------

interface CustomInputProps {
  value?: string;
  onClick?: () => void;
  onClear: () => void;
  showTime: boolean;
  hasValue: boolean;
  disabled?: boolean;
}

function CustomInput({ value, onClick, onClear, showTime, hasValue, disabled }: CustomInputProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`
        w-full flex items-center gap-2 px-3 py-2  border text-sm text-left
        transition-colors
        ${hasValue
          ? 'border-[var(--rule)] bg-[var(--bg-raised)] text-[var(--text-pri)] hover:border-[var(--slate)]'
          : 'border-[var(--rule)] bg-[var(--bg-raised)] text-[var(--text-ter)] hover:border-[var(--slate)]'
        }
        disabled:opacity-50 disabled:cursor-not-allowed
        focus:outline-none focus:border-[var(--slate)] focus:ring-2 focus:ring-indigo-100
      `}
    >
      {showTime
        ? <Clock className="h-4 w-4 shrink-0 text-[var(--text-ter)]" />
        : <CalendarDays className="h-4 w-4 shrink-0 text-[var(--text-ter)]" />
      }
      <span className="flex-1 truncate">{value || (hasValue ? '' : (showTime ? 'Pick date & time…' : 'Pick a date…'))}</span>
      {hasValue && (
        <span
          role="button"
          tabIndex={0}
          onClick={(e) => { e.stopPropagation(); onClear(); }}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); onClear(); } }}
          className="p-0.5 rounded hover:bg-[var(--hover-tint)] text-[var(--text-ter)] hover:text-[var(--text-sec)] cursor-pointer"
          aria-label="Clear date"
        >
          <X className="h-3.5 w-3.5" />
        </span>
      )}
    </button>
  );
}
